import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { collection, limit, orderBy, query, where } from 'firebase/firestore';
import { Plus, Search } from 'lucide-react';
import { EmptyState } from '../components/EmptyState';
import { JobRow, JobRowSkeleton } from '../components/JobRow';
import { buttonStyles } from '../components/ui/Button';
import { ReadFailure } from '../components/ReadFailure';
import { listenQuery } from '../lib/liveRead';
import { preBookedLast } from '../lib/schedule';
import { useLocalDay } from '../hooks/useLocalDay';
import { useAuth } from '../auth/AuthProvider';
import { db } from '../lib/firebase';
import { usePageTitle } from '../hooks/usePageTitle';
import { buildJobReadConstraints, finishedJobWindow, SUBCONTRACTOR_WINDOW_REFRESH_MS } from '../lib/jobScope';
import {
  canCreateJobs,
  canViewJobFinancials,
  JOB_ARCHIVE_STATES,
  JOB_BOARD_STATES,
  type Job,
  type JobState,
} from '../types/models';

type Tab = 'active' | 'archived';
type Filter = 'live' | 'mine' | JobState | 'all';

// Live means pending or in progress; cancelled has its own chip rather than sitting in
// the list. Counted chips, not a modal — the count is the reason to press one.
const LIVE_FILTERS: Array<{ value: Filter; label: string }> = [
  { value: 'live', label: 'Live' },
  { value: 'mine', label: 'Mine' },
  { value: 'pending', label: 'Pending' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'all', label: 'All' },
];

const ARCHIVE_FILTERS: Array<{ value: Filter; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'completed', label: 'Completed' },
  { value: 'archived', label: 'Archived' },
];

export default function JobsPage({ initialTab = 'active' }: { initialTab?: Tab }) {
  const archived = initialTab === 'archived';
  usePageTitle(archived ? 'Archive' : 'Live jobs');
  const { activeCompanyId, activeRole, memberships, user } = useAuth();
  const [filter, setFilter] = useState<Filter>(archived ? 'all' : 'live');
  const [search, setSearch] = useState('');
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [readAttempt, setReadAttempt] = useState(0);
  const showFinancials = canViewJobFinancials(activeRole ?? undefined);
  const localDay = useLocalDay();

  useEffect(() => { setFilter(archived ? 'all' : 'live'); setSearch(''); }, [archived]);

  useEffect(() => {
    if (!activeCompanyId) { setJobs([]); setLoading(false); return; }
    setLoading(true);
    setJobs([]);
    setError(null);
    // PR-26: one collection for the whole lifecycle — the page is a state filter, not a
    // different collection. Equality/`in` filters keep this provable by the rules'
    // per-document role check.
    const ref = collection(db, 'companies', activeCompanyId, 'jobs');
    const scope = buildJobReadConstraints({ activeCompanyId, role: activeRole, uid: user?.uid, memberships });
    if (scope.blocked) { setJobs([]); setError(scope.error); setLoading(false); return; }
    // PR-46: a subcontractor's archive carries the 48-hour window. Firestore
    // orders a range-filtered query by that field first, so it lists newest
    // finished first.
    const finishedWindow = archived ? finishedJobWindow(activeRole) : [];
    const q = query(
      ref,
      where('state', 'in', archived ? JOB_ARCHIVE_STATES : JOB_BOARD_STATES),
      ...scope.constraints,
      ...finishedWindow,
      orderBy(finishedWindow.length ? 'completedAt' : 'createdAt', 'desc'),
      limit(250)
    );
    const unsub = listenQuery(q, (snap) => {
      setJobs(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<Job, 'id'>) })));
      setLoading(false);
      setError(null);
    }, (err) => { console.error(err); setError(err.message); setLoading(false); });
    return () => unsub();
  }, [activeCompanyId, activeRole, memberships, archived, user?.uid, readAttempt]);

  // PR-46: restart a subcontractor's archive query with a fresh window.
  useEffect(() => {
    if (!archived || activeRole !== 'subcontractor') return;
    const timer = window.setInterval(() => setReadAttempt((n) => n + 1), SUBCONTRACTOR_WINDOW_REFRESH_MS);
    return () => window.clearInterval(timer);
  }, [archived, activeRole]);

  const matchesFilter = useMemo(() => (job: Job, value: Filter) => {
    if (value === 'all') return true;
    if (value === 'live') return job.state !== 'cancelled';
    if (value === 'mine') return !!user?.uid && job.assignedUserId === user.uid;
    return job.state === value;
  }, [user?.uid]);

  const counts = useMemo(() => {
    const filters = archived ? ARCHIVE_FILTERS : LIVE_FILTERS;
    return Object.fromEntries(filters.map((f) => [f.value, jobs.filter((job) => matchesFilter(job, f.value)).length]));
  }, [archived, jobs, matchesFilter]);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    const plain = s.replace(/\s+/g, '');
    // Live jobs: pre-booked ones after the rest, soonest first (PR-43, Bobby, 1 Nov 2026).
    const ordered = archived ? jobs : preBookedLast(jobs, new Date(localDay));
    return ordered.filter((job) => {
      if (!matchesFilter(job, filter)) return false;
      if (!s) return true;
      const haystack = [job.registrationNumber, job.make, job.model, job.customerName, job.pickupLocation,
        job.dropoffLocation, job.assignedUserDisplayName, job.assignedSubcontractorName, job.jobNumber]
        .filter(Boolean).map((v) => String(v).toLowerCase());
      // A registration searched without its space still finds the job.
      return haystack.some((v) => v.includes(s)) || (job.registrationNumber ?? '').toLowerCase().replace(/\s+/g, '').includes(plain);
    });
  }, [archived, filter, jobs, matchesFilter, search, localDay]);

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-[32px] font-bold leading-[38px] tracking-[-0.03em] text-ink">
            {archived ? 'Archive' : 'Live jobs'}
          </h1>
          <p className="mt-1.5 text-[15px] text-ink2">
            {archived
              ? 'Completed and archived work. Restore a job from its own page to reopen it.'
              : 'Pending and in progress. Completed jobs move to the Archive.'}
          </p>
        </div>
        {!archived && canCreateJobs(activeRole ?? undefined) && (
          <Link to="/jobs/new" className={buttonStyles('primary', 'md')}><Plus size={18} />New job</Link>
        )}
      </div>

      <div className="mt-5 flex h-11 items-center gap-2.5 rounded-[10px] bg-search px-3.5 text-ink3 focus-within:shadow-[inset_0_0_0_1.5px_var(--accent),0_0_0_3px_var(--accent-ring)]">
        <Search size={18} strokeWidth={1.8} aria-hidden />
        <input
          type="search"
          aria-label="Search jobs"
          placeholder="Search registration, customer, location"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full bg-transparent text-[15px] text-ink outline-none placeholder:text-ink3"
        />
      </div>

      <div className="mt-3.5 flex flex-wrap gap-2">
        {(archived ? ARCHIVE_FILTERS : LIVE_FILTERS).map((f) => {
          const selected = filter === f.value;
          return (
            <button
              key={f.value}
              type="button"
              aria-pressed={selected}
              onClick={() => setFilter(f.value)}
              className={`inline-flex h-9 items-center gap-2 rounded-full px-3.5 text-[14px] font-semibold transition-colors duration-[180ms] ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-surface ${
                selected ? 'bg-ink text-surface' : 'bg-elev text-ink2 shadow-[inset_0_0_0_1px_var(--strong)] hover:text-ink'
              }`}
            >
              {f.label}
              <span className={`font-mono text-[13px] font-medium ${selected ? 'opacity-80' : 'text-ink3'}`}>{counts[f.value] ?? 0}</span>
            </button>
          );
        })}
      </div>

      {error && <div className="mt-4"><ReadFailure message={error} retry={() => setReadAttempt((n) => n + 1)} /></div>}

      <div className="mt-4 flex flex-col gap-2">
        {loading ? (
          Array.from({ length: 5 }).map((_, i) => <JobRowSkeleton key={i} />)
        ) : error ? null : filtered.length === 0 ? (
          <EmptyState
            icon={Search}
            title={search ? 'Nothing matches that search' : archived ? 'No archived jobs' : 'No live jobs'}
            description={
              search ? 'Try a different registration, customer or location.'
                : archived ? 'Completed jobs appear here once archived.'
                : filter === 'live' ? 'Cancelled jobs are hidden from Live. Choose Cancelled to review them.'
                : 'There are no jobs matching this filter.'
            }
            action={!archived && canCreateJobs(activeRole ?? undefined)
              ? <Link to="/jobs/new" className={buttonStyles('primary', 'md')}><Plus size={18} />New job</Link>
              : undefined}
          />
        ) : (
          filtered.map((job) => (
            <JobRow
              key={job.id}
              job={job}
              to={archived ? `/jobs/archived/${job.id}` : `/jobs/${job.id}`}
              showFinancials={showFinancials}
            />
          ))
        )}
      </div>
    </div>
  );
}
